import * as React from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Context } from '@micro-frontend-react/core/lib/Context';
import type { Context as ReactContext } from 'react';
import type { IEmployeeExperienceContext } from '@micro-frontend-react/employee-experience/lib/IEmployeeExperienceContext';
import {
  Stack,
  Text,
  PrimaryButton,
  DefaultButton,
  MessageBar,
  MessageBarType,
  Spinner,
  SpinnerSize,
  Pivot,
  PivotItem,
  Icon,
} from '@fluentui/react';

import type { IPBTCase } from '../../../Interfaces/IPBTCase';
import { PBTApiEndpoints, PBTMessages, humanizeReminderMessage } from '../../../Services/PBTConstants';

interface ReminderResult {
  remindersSent: number;
  errors: number;
  messages: string[];
}

const CaseDetailPage: React.FC = () => {
  const { authClient } = React.useContext(Context as ReactContext<IEmployeeExperienceContext>);
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const caseId = Number(id);

  const [caseData, setCaseData] = React.useState<IPBTCase | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isSendingReminder, setIsSendingReminder] = React.useState(false);
  const [reminderMessage, setReminderMessage] = React.useState<{ type: MessageBarType; text: string } | null>(null);

  const fetchCaseDetail = React.useCallback(async () => {
    try {
      setIsLoading(true);
      const token = await authClient.acquireToken(__SCOPES__);
      const response = await fetch(PBTApiEndpoints.CaseDetail(caseId), {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error(`API returned ${response.status}`);
      const json = await response.json();
      setCaseData(json?.data ?? json);
    } catch {
      setCaseData(null);
    } finally {
      setIsLoading(false);
    }
  }, [caseId, authClient]);

  React.useEffect(() => {
    fetchCaseDetail();
  }, [fetchCaseDetail]);

  const handleSendReminder = React.useCallback(async () => {
    setIsSendingReminder(true);
    setReminderMessage(null);
    try {
      const token = await authClient.acquireToken(__SCOPES__);
      const response = await fetch(PBTApiEndpoints.SendReminder(caseId), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });

      if (!response.ok) throw new Error(`API returned ${response.status}`);

      const result: ReminderResult = await response.json();

      if (result.remindersSent > 0) {
        setReminderMessage({ type: MessageBarType.success, text: PBTMessages.ReminderSendSuccess });
        fetchCaseDetail();
      } else if (result.messages?.some((m) => m.includes('TermConversationDate'))) {
        setReminderMessage({ type: MessageBarType.info, text: PBTMessages.ReminderNotNeeded });
      } else if (result.messages?.some((m) => m.includes('NonResponsive'))) {
        setReminderMessage({ type: MessageBarType.warning, text: PBTMessages.ReminderAlreadyFlagged });
      } else {
        setReminderMessage({ type: MessageBarType.warning, text: humanizeReminderMessage(result.messages?.[0], caseData?.empName) });
      }
    } catch {
      setReminderMessage({ type: MessageBarType.error, text: PBTMessages.ReminderSendError });
    } finally {
      setIsSendingReminder(false);
    }
  }, [caseId, authClient, fetchCaseDetail, caseData?.empName]);

  const canSendReminder =
    caseData != null &&
    caseData.termConvDate == null &&
    !caseData.flags?.includes('Non-Responsive') &&
    !caseData.managerNonResponsive;

  if (isLoading) {
    return <Spinner size={SpinnerSize.large} label="Loading case details..." />;
  }

  if (!caseData) {
    return (
      <Stack tokens={{ padding: 20 }}>
        <MessageBar messageBarType={MessageBarType.error}>Case not found.</MessageBar>
      </Stack>
    );
  }

  return (
    <Stack tokens={{ padding: 20, childrenGap: 16 }}>
      {/* Case Header */}
      <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
        <Stack tokens={{ childrenGap: 4 }}>
          <Text variant="xLarge">{caseData.empName}</Text>
          <Text variant="medium">PERN: {caseData.pern} | CRM Case: {caseData.crmCase} | Manager: {caseData.manager}</Text>
        </Stack>
        <DefaultButton iconProps={{ iconName: 'Back' }} text="Back" onClick={() => history.goBack()} />
      </Stack>

      {/* Tabs */}
      <Pivot>
        <PivotItem headerText="Case Details">
          <Stack tokens={{ childrenGap: 8, padding: 16 }}>
            <Text><strong>Status:</strong> {caseData.status}</Text>
            <Text><strong>Engagement:</strong> {caseData.engModel}</Text>
            <Text><strong>HRC:</strong> {caseData.hrc}</Text>
            <Text><strong>Term Conv Date:</strong> {caseData.termConvDate ?? 'Not set'}</Text>
            <Text><strong>Eff Term Date:</strong> {caseData.effTermDate ?? 'Not set'}</Text>
            <Text><strong>Days in Status:</strong> {caseData.daysInStatus}</Text>
          </Stack>
        </PivotItem>

        <PivotItem headerText="Workflow Actions">
          <Stack tokens={{ childrenGap: 16, padding: 16 }}>
            <Text variant="large">Actions</Text>

            {/* Reminders Sent Count */}
            <Stack horizontal tokens={{ childrenGap: 24 }} verticalAlign="center"
              styles={{ root: { padding: '12px 16px', backgroundColor: '#f3f2f1', borderRadius: 4 } }}>
              <Stack tokens={{ childrenGap: 4 }}>
                <Text variant="small" styles={{ root: { color: '#605e5c' } }}>Reminders Sent</Text>
                <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>{caseData.reminderCount ?? 0}</Text>
              </Stack>
              {caseData.lastReminderSentAt && (
                <Stack tokens={{ childrenGap: 4 }}>
                  <Text variant="small" styles={{ root: { color: '#605e5c' } }}>Last Sent</Text>
                  <Text variant="medium">{new Date(caseData.lastReminderSentAt).toLocaleDateString()}</Text>
                </Stack>
              )}
              {caseData.managerNonResponsive && (
                <Stack tokens={{ childrenGap: 4 }}>
                  <Icon iconName="Warning" styles={{ root: { color: '#d83b01', fontSize: 20 } }} />
                  <Text variant="small" styles={{ root: { color: '#d83b01' } }}>Non-Responsive</Text>
                </Stack>
              )}
            </Stack>

            {reminderMessage && (
              <MessageBar
                messageBarType={reminderMessage.type}
                onDismiss={() => setReminderMessage(null)}
              >
                {reminderMessage.text}
              </MessageBar>
            )}

            {/* Action Buttons */}
            <Stack horizontal tokens={{ childrenGap: 12 }}>
              <PrimaryButton
                iconProps={{ iconName: 'Mail' }}
                text="Send Reminder"
                onClick={handleSendReminder}
                disabled={!canSendReminder || isSendingReminder}
              />
              {isSendingReminder && <Spinner size={SpinnerSize.small} />}
            </Stack>

            {!canSendReminder && caseData.termConvDate != null && (
              <MessageBar messageBarType={MessageBarType.info}>
                <Icon iconName="CheckMark" /> Term Conversation Date is already set. No reminder needed.
              </MessageBar>
            )}
          </Stack>
        </PivotItem>
      </Pivot>
    </Stack>
  );
};

export default CaseDetailPage;
